import { Row } from '@design-system/components/grid/grid';
import { useSortable } from '@dnd-kit/sortable';
import { FormProvider } from 'react-hook-form';
import { SectionName, SectionRenderer, DomainSection } from '../model/domain';
import { IntroductionSection } from './sections/IntroductionSection';
import { useSectionVM } from '../view-model/sectionVM';
import { LandingPageVM } from '../view-model/viewModel';
import { Card } from '@client/new/components/Card';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { CSS } from '@dnd-kit/utilities';
import { Label } from '@client/new/pages/challenges/view/ChallengeForm';

const StringifyRenderer = (p: any) => (
  <>
    <pre>section:{JSON.stringify(p.section, null, 2)}</pre>
    <pre>data:{JSON.stringify(p.data, null, 2)}</pre>
  </>
);
const rendersMap: {
  [K in SectionName]: SectionRenderer<K>;
} = {
  Introduction: IntroductionSection,
  Offer: StringifyRenderer,
};

export const SectionView = <T extends SectionName>(p: {
  section: DomainSection<T>;
  vm: LandingPageVM;
}) => {
  const sectionVM = useSectionVM({ sectionId: p.section.id, vm: p.vm });
  const Renderer = rendersMap[p.section.type] as any;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: p.section.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    margin: '8px 0',
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card>
        <Typography variant="bodyLarge">
          {p.section.type} ({p.section.id} (
          {p.vm.formsMap[p.section.id]?.getValues()?.headlineText}))
        </Typography>
        {sectionVM.isDirty() && <Label>[Modified]</Label>}
        {sectionVM.hasErrors() && <Label>[Errors]</Label>}

        <FormProvider {...sectionVM.form}>
          <form onSubmit={(e) => e.preventDefault()}>
            <Renderer
              section={p.section}
              data={p.vm.getSectionData(p.section.type)}
            />
          </form>
        </FormProvider>

        <Row gap="m">
          <Button onClick={sectionVM.validate} variant="text">
            Validate
          </Button>
          <Button onClick={sectionVM.save} variant="text">
            Save
          </Button>
        </Row>
      </Card>
    </div>
  );
};
